import type {EmploymentsResponse, SelfEmploymentProfile, SelfEmploymentProvider} from '@rocket-ui/lk-api';
import {isFinance, isRegularLkHost, isYpla} from 'shared/lib/isFinance';

export const getSelfEmploymentProvider = (): SelfEmploymentProvider => {
	// `lk.rocketwork.finance` — обычный ЛК: самозанятый rocketwork, не Альфа.
	if (isRegularLkHost()) return 'rocketwork';

	return isFinance() || isYpla() ? 'alfa' : 'rocketwork';
};

/** Альфа и бесплатный СМЗ идут одним партнёрским флоу, отличается только provider. */
export const isAlfaStyleSelfEmploymentProvider = (provider?: string | null): boolean => {
	return provider === 'alfa' || provider === 'free_smz';
};

/**
 * GET /employment: состояние СМЗ только из `selfEmploymentProfiles` нужного провайдера.
 * На финансе и ypla берём `alfa`, иначе `rocketwork`.
 * `lk.rocketwork.finance` — rocketwork, несмотря на TLD `.finance`.
 * Поле `selfEmployment` не используем.
 */
export const getSelfEmploymentSnapshot = (
	data: EmploymentsResponse,
	provider: SelfEmploymentProvider = getSelfEmploymentProvider(),
): SelfEmploymentProfile | undefined => {
	return data.selfEmploymentProfiles?.find((profile) => profile.provider === provider);
};

/**
 * Плитка самозанятого Альфа только из `selfEmploymentProfiles`:
 * есть профиль `alfa`, без ошибки и `active: true`.
 * Поле `selfEmployment` плитку не включает.
 */
export const hasActiveAlfaSelfEmployment = (
	data: Pick<EmploymentsResponse, 'selfEmploymentProfiles'> | null | undefined,
): boolean => {
	return isActiveSelfEmploymentProfile(data, 'alfa');
};

/** Плитка «Самозанятый Биржа» только если в `selfEmploymentProfiles` есть профиль `rocketwork`. */
export const hasRocketworkSelfEmploymentProfile = (
	data: Pick<EmploymentsResponse, 'selfEmploymentProfiles'> | null | undefined,
): boolean => {
	return Boolean(data?.selfEmploymentProfiles?.some((profile) => profile.provider === 'rocketwork'));
};

const hasSelfEmploymentProfileError = (
	profile: Pick<SelfEmploymentProfile, 'error' | 'status'>,
): boolean => {
	const {error, status} = profile;

	if (
		isNotSelfEmployedEmploymentStatus(status) ||
		isNotSelfEmployedEmploymentStatus(error) ||
		status === 'need_permissions' ||
		error === 'need_permissions' ||
		error === 'withdrawn'
	) {
		return true;
	}

	if (error === 'pending' || status === 'pending' || status === 'just_sent_inn_for_check') {
		return false;
	}

	return typeof error === 'string' && Boolean(error.trim());
};

/** Профиль провайдера подключён: `active: true` и без ошибки. */
export const isActiveSelfEmploymentProfile = (
	data: Pick<EmploymentsResponse, 'selfEmploymentProfiles'> | null | undefined,
	provider: SelfEmploymentProvider,
): boolean => {
	const profile = data?.selfEmploymentProfiles?.find((item) => item.provider === provider);

	if (!profile || profile.active !== true || hasSelfEmploymentProfileError(profile)) {
		return false;
	}

	return true;
};

/** СМЗ у партнёра подтверждён: профиль провайдера `validated: true` (или статус `validated`). */
export const isSelfEmploymentProfileValidated = (
	data: Pick<EmploymentsResponse, 'selfEmploymentProfiles'> | null | undefined,
	provider: SelfEmploymentProvider,
): boolean => {
	const profile = data?.selfEmploymentProfiles?.find((item) => item.provider === provider);

	return profile?.validated === true || profile?.status === 'validated';
};

type SelfEmploymentProfilesData = Pick<EmploymentsResponse, 'selfEmploymentProfiles'> | null | undefined;

const getSelfEmploymentAgreementProfile = (
	data: SelfEmploymentProfilesData,
	provider: SelfEmploymentProvider,
): SelfEmploymentProfile | undefined => {
	const profiles = data?.selfEmploymentProfiles;

	if (!profiles?.length) {
		return undefined;
	}

	const matched = profiles.find((profile) => profile.provider === provider);

	if (matched) {
		return matched;
	}

	// Старый ответ без provider: единственный профиль и есть текущий.
	if (profiles.every((profile) => !profile.provider)) {
		return profiles[0];
	}

	return undefined;
};

/** Профиль текущего провайдера (alfa / rocketwork / free_smz), не обязательно `profiles[0]`. */
const getCurrentSelfEmploymentProfile = (
	data: SelfEmploymentProfilesData,
	provider: SelfEmploymentProvider = getSelfEmploymentProvider(),
): SelfEmploymentProfile | undefined => {
	return getSelfEmploymentAgreementProfile(data, provider);
};

/** Анкета партнёра подписана: `agreement.status === 'signed'` у текущего провайдера. */
export const isSelfEmploymentAgreementSigned = (
	data: SelfEmploymentProfilesData,
	provider?: SelfEmploymentProvider,
): boolean => {
	return getCurrentSelfEmploymentProfile(data, provider)?.agreement?.status === 'signed';
};

/** Бэк присылает `not_selfemployed`; в кейсах встречается и `not_selfemployment`. */
export const isNotSelfEmployedEmploymentStatus = (status?: string | null): boolean => {
	return status === 'not_selfemployed' || status === 'not_selfemployment';
};

export const BIND_REQUEST_EXTERNAL_STATUS = 'BIND_REQUEST';

const readExternalStatus = (profile: SelfEmploymentProfile | undefined): string | null | undefined => {
	return profile?.externalStatus;
};

/**
 * «Что-то не так»: `agreement.status === 'signed'` и статус «не самозанятый».
 */
export const isSelfEmploymentSignedNotSelfEmployed = (
	data: SelfEmploymentProfilesData,
	provider?: SelfEmploymentProvider,
): boolean => {
	const profile = getCurrentSelfEmploymentProfile(data, provider);

	if (!profile || profile.agreement?.status !== 'signed') {
		return false;
	}

	return (
		isNotSelfEmployedEmploymentStatus(profile.status) || isNotSelfEmployedEmploymentStatus(profile.error)
	);
};

/**
 * Соглашение Альфа отправлено и ещё не подписано: `agreement.status === sent`.
 * На списке это не ожидание банка — по клику открывается фрейм.
 */
export const isSelfEmploymentAgreementSent = (
	data: SelfEmploymentProfilesData,
	provider?: SelfEmploymentProvider,
): boolean => {
	return getCurrentSelfEmploymentProfile(data, provider)?.agreement?.status === 'sent';
};

/**
 * «Нужен доступ в мой налог»: соглашение подписано, `status === pending`, `externalStatus === BIND_REQUEST`.
 */
export const isSelfEmploymentBindRequest = (
	data: SelfEmploymentProfilesData,
	provider?: SelfEmploymentProvider,
): boolean => {
	const profile = getCurrentSelfEmploymentProfile(data, provider);

	if (!profile || profile.agreement?.status !== 'signed') {
		return false;
	}

	if (profile.status !== 'pending' && profile.error !== 'pending') {
		return false;
	}

	return readExternalStatus(profile) === BIND_REQUEST_EXTERNAL_STATUS;
};

/**
 * «Ждём ответ ФНС»:
 * - `signed` + `just_sent_inn_for_check`;
 * - для Альфа ещё и `signed` + `pending` + любой `externalStatus`, кроме `BIND_REQUEST`.
 */
export const isSelfEmploymentFnsPending = (
	data: SelfEmploymentProfilesData,
	provider: SelfEmploymentProvider = getSelfEmploymentProvider(),
): boolean => {
	const profile = getCurrentSelfEmploymentProfile(data, provider);

	if (!profile || profile.agreement?.status !== 'signed') {
		return false;
	}

	if (profile.status === 'just_sent_inn_for_check') {
		return true;
	}

	if (profile.status !== 'pending' && profile.error !== 'pending') {
		return false;
	}

	return (
		isAlfaStyleSelfEmploymentProvider(provider) &&
		readExternalStatus(profile) !== BIND_REQUEST_EXTERNAL_STATUS
	);
};

/** `status` текущего провайдера — для опроса после «Доступ предоставлен» в Альфа-флоу. */
export const getSelfEmploymentProfilesFirstStatus = (
	data: SelfEmploymentProfilesData,
	provider?: SelfEmploymentProvider,
): string | null | undefined => {
	return getCurrentSelfEmploymentProfile(data, provider)?.status;
};

/**
 * Ссылка для iframe: есть `agreement.link` и статус ещё не `signed`.
 * null — открывать анкету не нужно.
 */
export const getSelfEmploymentAgreementLink = (
	data: Pick<EmploymentsResponse, 'selfEmploymentProfiles'> | null | undefined,
	provider: SelfEmploymentProvider,
): string | null => {
	const agreement = getSelfEmploymentAgreementProfile(data, provider)?.agreement;
	const link = agreement?.link?.trim();

	if (!link || agreement?.status === 'signed') {
		return null;
	}

	return link;
};

export const ALFA_AGREEMENT_CONFIRM_CAPTION = 'Подтвердите согласие с условием';

/** Ошибка на плитке, если фрейм соглашения уже открывали, а `agreement.status` всё ещё `sent`. */
export const ALFA_AGREEMENT_SIGN_ERROR = 'Подпишите соглашение';

export const ALFA_PERSONAL_DATA_CONSENT_MESSAGE = 'Подтвердите согласие на обработку ПД';

/** У профиля `alfa` согласие отправлено и ждёт подтверждения: `agreement.status === 'sent'`. */
export const isAlfaAgreementSent = (
	data: Pick<EmploymentsResponse, 'selfEmploymentProfiles'> | null | undefined,
): boolean => {
	const profile = data?.selfEmploymentProfiles?.find((item) => item.provider === 'alfa');

	return profile?.agreement?.status === 'sent';
};

/**
 * Ссылка согласия Альфа на экране статусов: в `selfEmploymentProfiles` есть
 * профиль `alfa` с непустым `agreement.link`, и согласие ещё не подписано.
 */
export const getAlfaSelfEmploymentAgreementLink = (
	data: Pick<EmploymentsResponse, 'selfEmploymentProfiles'> | null | undefined,
): string | null => {
	const profile = data?.selfEmploymentProfiles?.find((item) => item.provider === 'alfa');
	const link = profile?.agreement?.link?.trim();

	if (!link || profile?.agreement?.status === 'signed') {
		return null;
	}

	return link;
};

/** В profiles уже есть link (любой статус) — `bind_partner` вызывать не нужно. */
export const hasSelfEmploymentAgreementLink = (
	data: Pick<EmploymentsResponse, 'selfEmploymentProfiles'> | null | undefined,
	provider: SelfEmploymentProvider,
): boolean => {
	const link = getSelfEmploymentAgreementProfile(data, provider)?.agreement?.link?.trim();

	return Boolean(link);
};
